import {create} from 'zustand';
import {Payment, PaymentStatistics, PaymentFilterParams} from '../models/Payment';
import {Pagination} from '../../../Data/types/BaseResponse';
import {paymentsFactory} from './paymentsFactory';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';

interface PaymentsState {
  // Data
  payments: Payment[];
  statistics: PaymentStatistics | null;

  // Pagination
  paymentsPagination: Pagination | undefined;

  // Loading states
  loading: boolean;

  // Error states
  error: string | null;

  // Actions
  fetchPayments: () => Promise<void>;
  fetchPaymentsWithFilters: (params: PaymentFilterParams) => Promise<void>;
  fetchStatistics: () => Promise<void>;
  setPaymentsPagination: (pagination: Pagination | undefined) => void;
  refreshData: () => Promise<void>;
  clearErrors: () => void;
}

export const usePaymentsStore = create<PaymentsState>((set, get) => {
  // Initialize use cases using factory
  const {getPayments, getPaymentsWithFilters, getPaymentStatistics} = paymentsFactory.getUseCases();
  const errorHandlingService = StandardizedErrorHandler.getInstance();

  // Helper function to remove duplicates
  const removeDuplicatePayments = (payments: Payment[]): Payment[] => {
    const uniquePayments = payments.filter((payment, index, self) =>
      index === self.findIndex(p => p.id === payment.id)
    );
    return uniquePayments;
  };

  return {
    // Initial state
    payments: [],
    statistics: null,
    paymentsPagination: undefined,
    loading: false,
    error: null,

    // Actions
    fetchPayments: async () => {
      set({loading: true, error: null});
      try {
        const res = await getPayments.execute();
        const payments = res;

        // Remove duplicates
        const uniquePayments = removeDuplicatePayments(payments);
        if (uniquePayments.length !== payments.length) {
        }

        set({payments: uniquePayments, loading: false});
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({error: appError.message, loading: false});
        errorHandlingService.handleError(err); // For notifications/logging
      }
    },

    fetchPaymentsWithFilters: async (params: PaymentFilterParams) => {
      set({loading: true, error: null});
      try {
        const res = await getPaymentsWithFilters.execute(params);

        set({
          payments: res.items || [],
          paymentsPagination: res.pagination,
          loading: false
        });
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({error: appError.message, loading: false});
        errorHandlingService.handleError(err); // For notifications/logging
      }
    },


    fetchStatistics: async () => {
      set({loading: true, error: null});
      try {
        const res = await getPaymentStatistics.execute();
        set({statistics: res, loading: false});
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({error: appError.message, loading: false});
        errorHandlingService.handleError(err); // For notifications/logging
      }
    },

    setPaymentsPagination: (pagination: Pagination | undefined) => {
      set({paymentsPagination: pagination});
    },

    refreshData: async () => {
      const state = get();
      try {
        await Promise.all([
          state.fetchPayments(),
          state.fetchStatistics()
        ]);
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({error: appError.message});
        errorHandlingService.handleError(err); // For notifications/logging
      }
    },

    clearErrors: () => {
      set({error: null});
    }
  };
});
